import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Container,
    Paper,
    Box,
    Typography,
    Button,
    Chip,
    Divider,
    Grid,
    TextField,
    Alert,
    CircularProgress,
    Card,
    CardContent,
    IconButton,
    Tooltip,
    Tabs,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions
} from '@mui/material';
import {
    ArrowBack as BackIcon,
    Person as PersonIcon,
    CalendarToday as CalendarIcon,
    Assessment as AssessmentIcon,
    CheckCircle as CheckIcon,
    Pending as PendingIcon,
    Send as SendIcon,
    Visibility as ViewIcon,
    Code as CodeIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { useAuthStore, useAssignmentsStore } from '../stores';
import { coursesApi } from '../services/api';
import { ltiApi } from '../services/api/lti.api';

const AssignmentReviewPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { token } = useAuthStore();
    const { selectedAssignment, fetchAssignmentById } = useAssignmentsStore();

    const [loading, setLoading] = useState(true);
    const [submissions, setSubmissions] = useState([]);
    const [selectedSubmission, setSelectedSubmission] = useState(null);
    const [activeTab, setActiveTab] = useState(0);
    const [error, setError] = useState(null);

    // Feedback form
    const [feedbackDialogOpen, setFeedbackDialogOpen] = useState(false);
    const [feedbackForm, setFeedbackForm] = useState({
        grade: '',
        feedback_text: '',
        is_approved: false
    });
    const [submittingFeedback, setSubmittingFeedback] = useState(false);
    const [feedbackError, setFeedbackError] = useState(null);

    useEffect(() => {
        loadData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const loadData = async () => {
        setLoading(true);
        setError(null);

        try {
            // Load assignment
            await fetchAssignmentById(id, token);

            // Load submissions
            const response = await coursesApi.getAssignmentSubmissions(id, token);
            setSubmissions(response.data.results || response.data || []);
        } catch (error) {
            console.error('Error loading data:', error);
            setError(t('assignment_review.errors.load_failed'));
        } finally {
            setLoading(false);
        }
    };

    const handleSubmissionClick = (submission) => {
        setSelectedSubmission(submission);
        setActiveTab(1);
    };

    const handleOpenFeedbackDialog = (submission) => {
        setSelectedSubmission(submission);
        setFeedbackForm({
            grade: submission.grade || '',
            feedback_text: submission.feedback || '',
            is_approved: submission.is_reviewed || false
        });
        setFeedbackDialogOpen(true);
    };

    const handleSubmitFeedback = async () => {
        if (!selectedSubmission) return;

        setSubmittingFeedback(true);
        setFeedbackError(null);

        try {
            // Update submission with feedback
            await coursesApi.updateSubmission(
                selectedSubmission.id,
                {
                    grade: parseFloat(feedbackForm.grade) || null,
                    feedback: feedbackForm.feedback_text,
                    is_reviewed: true
                },
                token
            );

            // Send grade to LMS if configured
            if (selectedSubmission.external_system && feedbackForm.grade) {
                try {
                    const normalizedScore = parseFloat(feedbackForm.grade) / (selectedAssignment?.max_score || 100);
                    await ltiApi.sendGrade(
                        selectedSubmission.student.id,
                        id,
                        normalizedScore,
                        feedbackForm.feedback_text,
                        token
                    );
                } catch (ltiError) {
                    console.error('Error sending grade to LMS:', ltiError);
                    // Don't fail the whole operation if LMS sync fails
                }
            }

            // Reload data
            await loadData();

            setFeedbackDialogOpen(false);
            setSelectedSubmission(null);
        } catch (error) {
            console.error('Error submitting feedback:', error);
            setFeedbackError(
                error.response?.data?.message ||
                t('assignment_review.errors.feedback_failed')
            );
        } finally {
            setSubmittingFeedback(false);
        }
    };

    const getStatusConfig = (submission) => {
        if (submission.is_reviewed && submission.grade !== null && submission.grade !== undefined) {
            return {
                label: t('assignment_review.status.graded'),
                color: 'success',
                icon: <CheckIcon />
            };
        }
        if (submission.grade !== null && submission.grade !== undefined) {
            return {
                label: t('assignment_review.status.graded'),
                color: 'primary',
                icon: <AssessmentIcon />
            };
        }
        return {
            label: t('assignment_review.status.pending'),
            color: 'warning',
            icon: <PendingIcon />
        };
    };

    const calculateStatistics = () => {
        const total = submissions.length;
        const graded = submissions.filter(s => s.is_reviewed && s.grade !== null && s.grade !== undefined).length;
        const pending = total - graded;
        const avgGrade = submissions
            .filter(s => s.grade !== null && s.grade !== undefined)
            .reduce((sum, s) => sum + parseFloat(s.grade), 0) / (graded || 1);

        return { total, graded, pending, avgGrade };
    };

    const renderOverviewTab = () => {
        const stats = calculateStatistics();

        return (
            <Box>
                {/* Statistics Cards */}
                <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={12} sm={6} md={3}>
                        <Card>
                            <CardContent>
                                <Typography color="text.secondary" gutterBottom>
                                    {t('assignment_review.stats.total_submissions')}
                                </Typography>
                                <Typography variant="h4">{stats.total}</Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Card>
                            <CardContent>
                                <Typography color="text.secondary" gutterBottom>
                                    {t('assignment_review.stats.graded')}
                                </Typography>
                                <Typography variant="h4" color="primary.main">
                                    {stats.graded}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Card>
                            <CardContent>
                                <Typography color="text.secondary" gutterBottom>
                                    {t('assignment_review.stats.pending')}
                                </Typography>
                                <Typography variant="h4" color="warning.main">
                                    {stats.pending}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Card>
                            <CardContent>
                                <Typography color="text.secondary" gutterBottom>
                                    {t('assignment_review.stats.avg_grade')}
                                </Typography>
                                <Typography variant="h4" color="success.main">
                                    {stats.avgGrade.toFixed(1)}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>

                {/* Submissions Table */}
                <TableContainer component={Paper} variant="outlined">
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>{t('assignment_review.table.student')}</TableCell>
                                <TableCell>{t('assignment_review.table.submitted_at')}</TableCell>
                                <TableCell>{t('assignment_review.table.grade')}</TableCell>
                                <TableCell>{t('assignment_review.table.status')}</TableCell>
                                <TableCell align="right">{t('assignment_review.table.actions')}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {submissions.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={5} align="center">
                                        <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                                            {t('assignment_review.no_submissions')}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                submissions.map((submission) => {
                                    const statusConfig = getStatusConfig(submission);
                                    return (
                                        <TableRow
                                            key={submission.id}
                                            hover
                                            sx={{ cursor: 'pointer' }}
                                            onClick={() => handleSubmissionClick(submission)}
                                        >
                                            <TableCell>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <PersonIcon fontSize="small" color="action" />
                                                    <Typography variant="body2">
                                                        {submission.user?.username || submission.user?.email || 'Unknown'}
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" color="text.secondary">
                                                    {new Date(submission.submitted_at).toLocaleString()}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                {submission.grade !== null && submission.grade !== undefined ? (
                                                    <Chip
                                                        label={`${submission.grade} / ${selectedAssignment?.max_score || 100}`}
                                                        size="small"
                                                        color="primary"
                                                    />
                                                ) : (
                                                    <Typography variant="body2" color="text.secondary">
                                                        -
                                                    </Typography>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    icon={statusConfig.icon}
                                                    label={statusConfig.label}
                                                    color={statusConfig.color}
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell align="right">
                                                <Tooltip title={t('assignment_review.actions.give_feedback')}>
                                                    <IconButton
                                                        size="small"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleOpenFeedbackDialog(submission);
                                                        }}
                                                    >
                                                        <SendIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title={t('assignment_review.actions.view_details')}>
                                                    <IconButton
                                                        size="small"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleSubmissionClick(submission);
                                                        }}
                                                    >
                                                        <ViewIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>
        );
    };

    const renderSubmissionDetailsTab = () => {
        if (!selectedSubmission) {
            return (
                <Alert severity="info">
                    {t('assignment_review.select_submission')}
                </Alert>
            );
        }

        return (
            <Box>
                {/* Student Info */}
                <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            <Typography variant="subtitle2" color="text.secondary">
                                {t('assignment_review.student')}
                            </Typography>
                            <Typography variant="body1">
                                {selectedSubmission.user?.username || selectedSubmission.user?.email}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Typography variant="subtitle2" color="text.secondary">
                                {t('assignment_review.submitted_at')}
                            </Typography>
                            <Typography variant="body1">
                                {new Date(selectedSubmission.submitted_at).toLocaleString()}
                            </Typography>
                        </Grid>
                    </Grid>
                </Paper>

                {/* Solution */}
                <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                    <Typography variant="h6" gutterBottom>
                        {t('assignment_review.solution')}
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    <Box sx={{
                        p: 2,
                        bgcolor: 'background.default',
                        borderRadius: 1,
                        fontFamily: 'monospace',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word'
                    }}>
                        {selectedSubmission.content || selectedSubmission.code || t('assignment_review.no_solution')}
                    </Box>
                </Paper>

                {/* Existing Feedback */}
                {selectedSubmission.feedback && (
                    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                            <Typography variant="h6">
                                {t('assignment_review.feedback')}
                            </Typography>
                            {selectedSubmission.reviewed_by && (
                                <Chip
                                    label={t('assignment_review.feedback_by.teacher')}
                                    size="small"
                                    color="success"
                                />
                            )}
                        </Box>
                        <Divider sx={{ mb: 2 }} />
                        <Box sx={{ '& p': { mb: 1 }, '& pre': { bgcolor: 'background.default', p: 1, borderRadius: 1 } }}>
                            <ReactMarkdown
                                components={{
                                    code({ node, inline, className, children, ...props }) {
                                        const match = /language-(\w+)/.exec(className || '');
                                        return !inline && match ? (
                                            <SyntaxHighlighter
                                                style={oneDark}
                                                language={match[1]}
                                                PreTag="div"
                                                {...props}
                                            >
                                                {String(children).replace(/\n$/, '')}
                                            </SyntaxHighlighter>
                                        ) : (
                                            <code className={className} {...props}>
                                                {children}
                                            </code>
                                        );
                                    }
                                }}
                            >
                                {selectedSubmission.feedback}
                            </ReactMarkdown>
                        </Box>
                    </Paper>
                )}

                {/* Action Button */}
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                    <Button
                        variant="contained"
                        size="large"
                        startIcon={<SendIcon />}
                        onClick={() => handleOpenFeedbackDialog(selectedSubmission)}
                    >
                        {selectedSubmission.feedback
                            ? t('assignment_review.actions.update_feedback')
                            : t('assignment_review.actions.give_feedback')}
                    </Button>
                </Box>
            </Box>
        );
    };

    if (loading) {
        return (
            <Container maxWidth="lg" sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress />
            </Container>
        );
    }

    if (error) {
        return (
            <Container maxWidth="lg" sx={{ mt: 4 }}>
                <Alert severity="error">{error}</Alert>
            </Container>
        );
    }

    return (
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
            {/* Header */}
            <Box sx={{ mb: 3 }}>
                <Button
                    startIcon={<BackIcon />}
                    onClick={() => navigate(-1)}
                    sx={{ mb: 2 }}
                >
                    {t('common.back')}
                </Button>

                <Typography variant="h4" gutterBottom>
                    {selectedAssignment?.title}
                </Typography>

                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                    <Chip
                        icon={<CodeIcon />}
                        label={t(`create_assignment.assignment_types.${selectedAssignment?.assignment_type || 'sandbox'}`)}
                        size="small"
                    />
                    {selectedAssignment?.deadline && (
                        <Chip
                            icon={<CalendarIcon />}
                            label={`${t('assignment_review.deadline')}: ${new Date(selectedAssignment.deadline).toLocaleDateString()}`}
                            size="small"
                        />
                    )}
                    <Chip
                        label={`${t('assignment_review.max_score')}: ${selectedAssignment?.max_score || 100}`}
                        size="small"
                        color="primary"
                    />
                </Box>
            </Box>

            {/* Tabs */}
            <Paper sx={{ mb: 2 }}>
                <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)}>
                    <Tab label={t('assignment_review.tabs.overview')} />
                    <Tab label={t('assignment_review.tabs.submission_details')} />
                </Tabs>
            </Paper>

            {/* Tab Content */}
            {activeTab === 0 && renderOverviewTab()}
            {activeTab === 1 && renderSubmissionDetailsTab()}

            {/* Feedback Dialog */}
            <Dialog
                open={feedbackDialogOpen}
                onClose={() => setFeedbackDialogOpen(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    {t('assignment_review.feedback_dialog.title')}
                </DialogTitle>
                <DialogContent dividers>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <TextField
                            fullWidth
                            type="number"
                            label={t('assignment_review.feedback_dialog.grade')}
                            value={feedbackForm.grade}
                            onChange={(e) => setFeedbackForm(prev => ({ ...prev, grade: e.target.value }))}
                            inputProps={{
                                min: 0,
                                max: selectedAssignment?.max_score || 100,
                                step: 0.1
                            }}
                            helperText={`${t('assignment_review.feedback_dialog.max_score')}: ${selectedAssignment?.max_score || 100}`}
                        />

                        <TextField
                            fullWidth
                            multiline
                            rows={8}
                            label={t('assignment_review.feedback_dialog.feedback_text')}
                            value={feedbackForm.feedback_text}
                            onChange={(e) => setFeedbackForm(prev => ({ ...prev, feedback_text: e.target.value }))}
                            placeholder={t('assignment_review.feedback_dialog.feedback_placeholder')}
                        />


                        {feedbackError && (
                            <Alert severity="error" onClose={() => setFeedbackError(null)}>
                                {feedbackError}
                            </Alert>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setFeedbackDialogOpen(false)}>
                        {t('common.cancel')}
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleSubmitFeedback}
                        disabled={submittingFeedback}
                        startIcon={submittingFeedback ? <CircularProgress size={20} /> : <SendIcon />}
                    >
                        {t('assignment_review.feedback_dialog.submit')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default AssignmentReviewPage;
